import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface IdeaRow {
  readonly ideaId: string;
  readonly ideaCode: string;
  readonly ideaTitle: string;
  readonly innovationCategory: string;
  readonly totalScore: string | null;
  readonly status: string;
}

const CATEGORIES = [
  '', 'PROCESS', 'PRODUCT', 'TECHNOLOGY', 'BUSINESS_MODEL',
  'CUSTOMER_EXPERIENCE', 'OPERATIONAL', 'OTHER',
];
const STATUSES = ['', 'DRAFT', 'SUBMITTED', 'UNDER_REVIEW', 'SCORED', 'APPROVED', 'CONVERTED', 'REJECTED'];
const CREATE_CATEGORIES = CATEGORIES.filter(Boolean);

/**
 * M33 — Idea Management Dashboard (§31 / §8). Register of ideas (T434) captured
 * from the workforce, carrying innovation category, weighted score and the
 * capture→score→approve→convert lifecycle. Supports capturing an idea and
 * driving it — submit, review, score (three-axis), approve and reject — each
 * wired to the guarded /v1/pm/ideas write API. Filterable by category and status.
 */
export function IdeaManagementDashboard(): ReactNode {
  const { decimal } = useBusinessNumber();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<IdeaRow[]>([]);
  const [innovationCategory, setInnovationCategory] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [newTitle, setNewTitle] = useState('');
  const [newCategory, setNewCategory] = useState('PROCESS');
  const [scores, setScores] = useState<Record<string, { f: string; i: string; v: string }>>({});
  const [rejReasons, setRejReasons] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (innovationCategory) params.set('innovationCategory', innovationCategory);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: IdeaRow[] }>(`/v1/pm/ideas${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, innovationCategory, status, reload]);

  const failMessage = (err: unknown): string =>
    err instanceof ApiError && err.status === 403
      ? t('m33.idea.notAuthorised')
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newTitle.trim()) { setActionError(t('m33.idea.validation')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/pm/ideas`, {
        method: 'POST',
        body: { ideaTitle: newTitle.trim(), innovationCategory: newCategory },
        token,
      });
      setNotice(t('m33.idea.captured', { name: newTitle.trim() }));
      setNewTitle('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const act = async (r: IdeaRow, path: string, label: string, body?: Record<string, unknown>): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/pm/ideas/${r.ideaId}/${path}`, { method: 'POST', body: body ?? {}, token });
      setNotice(t('m33.common.notice', { code: r.ideaCode, label }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    }
  };

  const scoreOf = (id: string) => scores[id] ?? { f: '', i: '', v: '' };

  return (
    <main aria-label="idea-management-dashboard">
      <h1>{t('m33.idea.title')}</h1>

      <section aria-label="capture-idea">
        <h2>{t('m33.idea.captureHeading')}</h2>
        <form aria-label="idea-form" onSubmit={(e) => void create(e)}>
          <label>{t('m33.common.title')}<input aria-label="field-idea-title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} /></label>
          <label>{t('m33.common.category')}
            <select aria-label="field-idea-category" value={newCategory} onChange={(e) => setNewCategory(e.target.value)}>
              {CREATE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <Button variant="primary" type="submit" aria-label="submit-idea" disabled={busy}>{busy ? t('m33.idea.capturing') : t('m33.idea.captureBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="idea-error">{actionError}</p>}
        {notice && <p aria-label="idea-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {t('m33.common.category')}
          <select aria-label="filter-category" value={innovationCategory} onChange={(e) => setInnovationCategory(e.target.value)}>
            {CATEGORIES.map((c) => <option key={c || 'all'} value={c}>{c || t('m33.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m33.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m33.common.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m33.common.loading')}</p> : (
        <>
          <p aria-label="idea-count">{t('m33.idea.count', { n: rows.length })}</p>
          <DataTable
            ariaLabel="idea-table"
            rows={rows}
            rowKey={(r) => r.ideaId}
            columns={[
              { key: 'ideaCode', header: t('m33.common.code'), render: (r) => r.ideaCode },
              { key: 'ideaTitle', header: t('m33.common.title'), render: (r) => r.ideaTitle },
              { key: 'innovationCategory', header: t('m33.common.category'), render: (r) => r.innovationCategory },
              { key: 'totalScore', header: t('m33.idea.colScore'), render: (r) => decimal(r.totalScore) },
              { key: 'status', header: t('m33.common.status'), render: (r) => r.status },
              { key: 'actions', header: t('m33.common.actions'), render: (r) => (
                <>
                  {r.status === 'DRAFT' && (
                    <Button variant="secondary" type="button" aria-label={`submit-${r.ideaCode}`} onClick={() => void act(r, 'submit', t('m33.idea.act.submitted'))}>{t('m33.idea.btnSubmit')}</Button>
                  )}
                  {r.status === 'SUBMITTED' && (
                    <Button variant="secondary" type="button" aria-label={`review-${r.ideaCode}`} onClick={() => void act(r, 'review', t('m33.idea.act.underReview'))}>{t('m33.idea.btnReview')}</Button>
                  )}
                  {(r.status === 'UNDER_REVIEW' || r.status === 'SCORED') && (
                    <>
                      <input aria-label={`feasibility-${r.ideaCode}`} type="number" value={scoreOf(r.ideaId).f} onChange={(e) => setScores((m) => ({ ...m, [r.ideaId]: { ...scoreOf(r.ideaId), f: e.target.value } }))} placeholder={t('m33.idea.feasPh')} />
                      <input aria-label={`impact-${r.ideaCode}`} type="number" value={scoreOf(r.ideaId).i} onChange={(e) => setScores((m) => ({ ...m, [r.ideaId]: { ...scoreOf(r.ideaId), i: e.target.value } }))} placeholder={t('m33.idea.impactPh')} />
                      <input aria-label={`value-${r.ideaCode}`} type="number" value={scoreOf(r.ideaId).v} onChange={(e) => setScores((m) => ({ ...m, [r.ideaId]: { ...scoreOf(r.ideaId), v: e.target.value } }))} placeholder={t('m33.idea.valuePh')} />
                      <Button variant="secondary" type="button" aria-label={`score-${r.ideaCode}`} onClick={() => void act(r, 'score', t('m33.idea.act.scored'), { feasibilityScore: Number(scoreOf(r.ideaId).f || 0), impactScore: Number(scoreOf(r.ideaId).i || 0), valueScore: Number(scoreOf(r.ideaId).v || 0) })}>{t('m33.idea.btnScore')}</Button>
                    </>
                  )}
                  {r.status === 'SCORED' && (
                    <Button variant="secondary" type="button" aria-label={`approve-${r.ideaCode}`} onClick={() => void act(r, 'approve', t('m33.idea.act.approved'))}>{t('m33.idea.btnApprove')}</Button>
                  )}
                  {!['CONVERTED', 'REJECTED'].includes(r.status) && (
                    <>
                      <input aria-label={`reject-reason-${r.ideaCode}`} value={rejReasons[r.ideaId] ?? ''} onChange={(e) => setRejReasons((m) => ({ ...m, [r.ideaId]: e.target.value }))} placeholder={t('m33.common.reason')} />
                      <Button variant="danger" type="button" aria-label={`reject-${r.ideaCode}`} onClick={() => void act(r, 'reject', t('m33.idea.act.rejected'), { reason: rejReasons[r.ideaId] || undefined })}>{t('m33.idea.btnReject')}</Button>
                    </>
                  )}
                </>
              ) },
            ]}
          />
        </>
      )}
    </main>
  );
}
